import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function BookDemo() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", sector: "", message: "" });
  const [sectors, setSectors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/catalog/sectors").then(r => setSectors(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/demo-requests", form);
      setDone(true);
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
      toast.success("Strategy call requested. Our team will respond within 1 business day.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <section className="border-b border-slate-200 relative overflow-hidden">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-70" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">Book a strategy call</p>
            <h1 className="mt-3 text-5xl sm:text-6xl tracking-[-0.04em] font-semibold text-slate-900 leading-[1.02]">30 minutes. Bring your numbers. Leave with a routing plan.</h1>
            <ul className="mt-8 space-y-3 text-slate-700">
              {["Live walkthrough of your sector economics", "Routing diagram tailored to your campaigns", "AI script + qualification flow preview", "Clear next steps and a sandbox account"].map((l, i) => (
                <li key={i} className="flex items-center gap-2"><CheckCircle2 className="size-4 text-primary" /> {l}</li>
              ))}
            </ul>
          </div>

          <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-soft" data-testid="demo-form">
            {done ? (
              <div className="text-center py-10">
                <div className="inline-grid place-items-center size-12 rounded-full bg-emerald-50 text-emerald-700"><CheckCircle2 className="size-6" /></div>
                <h3 className="mt-5 text-2xl font-semibold text-slate-900">Request received.</h3>
                <p className="mt-2 text-slate-600">We'll be in touch within one business day.</p>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div><Label>Full name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="demo-name" /></div>
                  <div><Label>Work email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="demo-email" /></div>
                  <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="demo-company" /></div>
                  <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="demo-phone" /></div>
                  <div className="sm:col-span-2">
                    <Label>Primary sector</Label>
                    <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
                      <SelectTrigger data-testid="demo-sector"><SelectValue placeholder="Select sector" /></SelectTrigger>
                      <SelectContent>
                        {sectors.map(s => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
                        <SelectItem value="other" data-testid="demo-sector-other">Other / not listed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2"><Label>Tell us what you're solving</Label><Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="demo-message" /></div>
                </div>
                <Button disabled={busy} type="submit" className="mt-6 w-full rounded-full h-11" data-testid="demo-submit">{busy ? "Submitting…" : "Request strategy call"}</Button>
              </>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}
